import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, DefaultHttpStack } from "tus-js-client";
import { useNavigate } from "react-router-dom";

const QuestionForm = () => {
  // Question fields
  const [qid, setqid] = useState(0);
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState(["", "", "", "", "", ""]);
  const [answer, setAnswer] = useState("");
  const [hints, setHints] = useState("");
  const [priority, setPriority] = useState(0);
  const [otherInfo, setOtherInfo] = useState("");

  // Video upload fields
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadStatus, setUploadStatus] = useState("idle");
  const navigate = useNavigate();

  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingCategories(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/fetch-categories?taxonomy=Quiz`
        );

        const categoriesData = await response.json();

        setCategories(categoriesData.data.terms || []);
      } catch (err) {
        setError("Failed to load categories");
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchData();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setVideoUrl("");
    setError(null);
  };

  const toggleExpand = (catID) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catID]: !prev[catID],
    }));
  };

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleUpload = async () => {
    if (!questionText) {
      setError("Please enter a question.");
      return;
    }
    if (!answer) {
      setError("Please select answer.");
      return;
    }
    const filledOptions = options.filter((opt) => opt.trim() !== "");
    if (filledOptions.length < 4) {
      setError("Please enter at least 4 options.");
      return;
    }
    if (!file) {
      setError("Please select an explainer video.");
      return;
    }
    const selectedCategoryIds = Object.keys(selectedCategories).filter(
      (id) => selectedCategories[id]
    );
    if (selectedCategoryIds.length === 0) {
      setError("Please select at least one category.");
      return;
    }

    try {
      setUploadStatus("initiating");
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload/questions/init`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: file.name,
            file_size: file.size,
          }),
        }
      );

      const initData = await response.json();
      if (!response.ok)
        throw new Error(initData.message || "Failed to initiate upload");

      const vimeoId = initData.data.vimeo_id;
      // Step 2: Configure TUS upload for Vimeo
      setUploadStatus("uploading");
      setUploading(true);
      setProgress(0);

      const upload = new Upload(file, {
        endpoint: initData.data.upload_link,
        uploadUrl: initData.data.upload_link,
        retryDelays: [0, 3000, 5000, 10000],
        chunkSize: 5 * 1024 * 1024, // 5MB chunks
        metadata: {
          filename: btoa(unescape(encodeURIComponent(file.name))),
          filetype: btoa(unescape(encodeURIComponent(file.type))),
        },
        headers: {},
        httpStack: new DefaultHttpStack(),
        onError: (error) => {
          console.error("Upload failed:", error);
          setError(`Upload failed: ${error.message}`);
          setUploadStatus("error");
          setUploading(false);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setProgress((bytesUploaded / bytesTotal) * 100);
        },
        onSuccess: async () => {
          try {
            setUploadStatus("finalizing");
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/questions`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  name,
                  vimeoId: vimeoId,
                  qid: qid,
                  question_text: questionText,
                  option_1: options[0],
                  option_2: options[1],
                  option_3: options[2],
                  option_4: options[3],
                  option_5: options[4],
                  option_6: options[5],
                  answer: answer,
                  other_info: otherInfo,
                  hints: hints,
                  priority: priority,
                  categories: Object.keys(selectedCategories).filter(
                    (id) => selectedCategories[id]
                  ),
                }),
              }
            );

            const finalData = await response.json();
            if (!response.ok)
              throw new Error(finalData.message || "Finalization failed");

            setVideoUrl(finalData.data.video_url);

            setUploadStatus("completed");
            navigate("/xl-admin/questions");
          } catch (err) {
            setError(
              `Upload completed but finalization failed: ${err.message}`
            );
            setUploadStatus("error");
          } finally {
            setUploading(false);
          }
        },
      });

      upload.options.method = "PATCH";

      upload.start();
    } catch (err) {
      console.error("Upload process error:", err);
      setError(err.message);
      setUploadStatus("error");
      setUploading(false);
    }
  };

  const getSubcategories = (parentId) => {
    return categories.filter((cat) => cat.parent_id === parentId);
  };

  const isLeafCategory = (categoryId) => {
    return !categories.some((cat) => cat.parent_id === categoryId);
  };

  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories[category.id];
    const isSelectable = category.taxonomy !== null;

    return (
      <div
        key={`cat-${category.id}`}
        className="mb-2"
        style={{ marginLeft: `${level * 16}px` }}
      >
        <div className="d-flex align-items-center">
          {isSelectable ? (
            <>
              <input
                type="checkbox"
                id={`cat-${category.id}`}
                checked={!!selectedCategories[category.id]}
                onChange={() => toggleCategorySelection(category.id)}
                className="form-check-input me-2"
              />
              <label
                htmlFor={`cat-${category.id}`}
                className="form-check-label"
                style={{ cursor: "pointer" }}
              >
                {category.name}
              </label>
            </>
          ) : (
            <div
              className="d-flex align-items-center"
              style={{ cursor: hasChildren ? "pointer" : "default" }}
              onClick={() => hasChildren && toggleExpand(category.id)}
            >
              {hasChildren && (
                <span className="me-2">{isExpanded ? "−" : "+"}</span>
              )}
              <span>{category.name}</span>
            </div>
          )}
        </div>

        {isExpanded &&
          hasChildren &&
          category.children.map((child) =>
            renderCategoryTree(child, level + 1)
          )}
      </div>
    );
  };

  const getButtonText = () => {
    switch (uploadStatus) {
      case "initiating":
        return "Preparing upload...";
      case "uploading":
        return `Uploading... ${Math.round(progress)}%`;
      case "finalizing":
        return "Finalizing...";
      case "completed":
        return "Upload Complete";
      case "error":
        return "Try Again";
      default:
        return "Add Now";
    }
  };

  return (
    <div className="container">
      <h2 className="mb-4">Add a Question</h2>

      <div className="row">
        <div className="col-md-8">
          <div className="mb-3">
            <label className="form-label">ID:</label>
            <input
              type="number"
              className="form-control"
              value={qid}
              onChange={(e) => setqid(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Question:</label>
            <textarea
              className="form-control"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Options</label>
            {options.map((option, index) => (
              <input
                key={`option-${index}`}
                type="text"
                className="form-control mb-2"
                placeholder={`Option ${index + 1}`}
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
              />
            ))}
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Answer</label>
              <select
                className="form-select"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                required
              >
                <option value="">Select answer</option>
                {options.map(
                  (option, index) =>
                    option && (
                      <option key={`answer-${index}`} value={`${index + 1}`}>
                        Option {index + 1}: {option}
                      </option>
                    )
                )}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Hints</label>
            <textarea
              className="form-control"
              value={hints}
              onChange={(e) => setHints(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Other Info</label>
            <textarea
              className="form-control"
              value={otherInfo}
              onChange={(e) => setOtherInfo(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Explainer video:</label>
            <input
              type="file"
              accept="video/mp4,video/quicktime"
              className="form-control"
              onChange={handleFileChange}
            />
            {file && (
              <small className="text-muted">Selected: {file.name}</small>
            )}
            {videoUrl && (
              <div className="mt-2">
                <small className="text-success">Video URL: {videoUrl}</small>
              </div>
            )}
            {uploading && (
              <div className="progress mt-2">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {progress}%
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          <button
            className="btn btn-lime-green text-white"
            onClick={handleUpload}
            disabled={
              uploading ||
              uploadStatus === "initiating" ||
              uploadStatus === "verifying"
            }
          >
            {getButtonText()}
          </button>
        </div>

        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Categories</h5>
            </div>
            <div
              className="card-body"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {loadingCategories ? (
                <p>Loading categories...</p>
              ) : (
                <div className="category-tree">
                  {Array.isArray(categories) &&
                    categories.map((category) => renderCategoryTree(category))}
                </div>
              )}
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Priority</h5>
            </div>
            <div className="card-body">
              <select
                className="form-select"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value))}
              >
                {[...Array(11).keys()].map((num) => (
                  <option key={`priority-${num}`} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionForm;
